import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Linkedin, MessageCircle } from "lucide-react";
import LanguageToggle from "@/components/LanguageToggle";
import { useLocale } from "@/i18n/useLocale";

const Footer = () => {
  const { t } = useTranslation();
  const { L } = useLocale();

  return (
    <footer className="bg-brand-black text-brand-cream/90" data-testid="site-footer">
      <div className="container-wide py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        <div className="md:col-span-4">
          <Link to={L("/")} className="font-display text-3xl tracking-tight">SPIRUVA<sup className="text-[0.42em] align-super ml-px font-medium tracking-normal">™</sup></Link>
          <p className="font-display italic text-brand-gold/90 mt-2 text-lg">{t("footer.tagline")}</p>
          <p className="text-sm text-brand-cream/65 mt-6 leading-relaxed max-w-sm">{t("footer.platform_blurb")}</p>
          <p className="text-[11px] text-brand-cream/45 mt-5 font-mono uppercase tracking-[0.18em] leading-relaxed max-w-sm">{t("footer.readiness_note")}</p>
          <div className="flex items-center gap-2 mt-6 text-sm text-brand-cream/70">
            <MapPin size={14} className="text-brand-gold" />
            <span>{t("footer.location")}</span>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="eyebrow-light mb-5">{t("footer.grade_portfolio")}</p>
          <ul className="space-y-3 text-sm">
            <li><Link to={L("/products")} className="hover:text-brand-gold transition">Phycocyanin E18</Link></li>
            <li><Link to={L("/products")} className="hover:text-brand-gold transition">Phycocyanin E25</Link></li>
            <li><Link to={L("/products")} className="hover:text-brand-gold transition">Phycocyanin E30</Link></li>
            <li><Link to={L("/products")} className="hover:text-brand-gold transition">Phycocyanin E40</Link></li>
            <li><Link to={L("/products")} className="hover:text-brand-gold transition">Organic Spirulina</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <p className="eyebrow-light mb-5">{t("footer.platform")}</p>
          <ul className="space-y-3 text-sm">
            <li><Link to={L("/about")} className="hover:text-brand-gold transition">{t("nav.about")}</Link></li>
            <li><Link to={L("/science")} className="hover:text-brand-gold transition">{t("nav.science")}</Link></li>
            <li><Link to={L("/markets")} className="hover:text-brand-gold transition">{t("nav.markets")}</Link></li>
            <li><Link to={L("/journal")} className="hover:text-brand-gold transition">{t("resources_menu.journal")}</Link></li>
            <li><Link to={L("/resources")} className="hover:text-brand-gold transition">{t("nav.resources")}</Link></li>
            <li><Link to={L("/certifications")} className="hover:text-brand-gold transition">{t("resources_menu.certifications")}</Link></li>
            <li><Link to={L("/distributor-portal")} className="hover:text-brand-gold transition">{t("footer.distributor")}</Link></li>
            <li><Link to={L("/privacy")} className="hover:text-brand-gold transition" data-testid="footer-link-privacy">Privacy</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="eyebrow-light mb-5">{t("footer.partner_desk")}</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Mail size={14} className="text-brand-gold" /> export@spiruva.com</li>
            <li className="flex items-center gap-3"><MessageCircle size={14} className="text-brand-gold" /> {t("footer.whatsapp_note")}</li>
            <li className="flex items-center gap-3"><Linkedin size={14} className="text-brand-gold" /> linkedin.com/company/spiruva</li>
          </ul>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Link to={L("/reserve-allocation")} data-testid="footer-cta-reserve" className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-brand-cream transition min-h-[44px]">
              {t("footer.reserve")}
            </Link>
            <Link to={L("/distributor-portal")} data-testid="footer-cta-distributor" className="inline-flex items-center justify-center gap-2 border border-brand-cream/25 text-brand-cream/90 px-5 py-3 text-[11px] uppercase tracking-[0.18em] hover:bg-brand-cream/10 hover:text-brand-cream transition min-h-[44px]">
              {t("footer.distributor")}
            </Link>
          </div>
          <div className="mt-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-cream/45 mb-2">◦  {t("nav.language")}</p>
            <LanguageToggle variant="footer" />
          </div>
        </div>
      </div>

      <div className="border-t border-brand-cream/10">
        <div className="container-wide py-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-[11px] font-mono uppercase tracking-widest text-brand-cream/50">
          <p>{t("footer.initiative")} <span className="text-brand-gold/90">Carbofixers</span> · FSSAI · APEDA · DGFT</p>
          <p>{t("footer.copyright")}</p>
          <Link to="/admin" data-testid="footer-admin-link" className="hover:text-brand-gold transition">{t("footer.admin")}</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
